import Image from 'next/image'
import React from 'react'

import { Button } from '@/components/Button/Button'
import { pxToRem } from '@/shared/styles/themes'
import { Box, Container, styled, Typography } from '@mui/material'

const steps = [
  { number: '1', title: 'Pick a Story', description: 'Browse our library of hand-crafted tales' },
  { number: '2', title: 'Add Your Child\'s Look', description: 'Choose features that match your child' },
  { number: '3', title: 'See Every Page Personalised', description: 'Your child appears as the hero on every spread, in seconds' },
]

const characters = [
  { src: '/images/characters/straight-blonde-newborn-neutral-sticker.png', alt: 'Blonde character' },
  { src: '/images/characters/straight-blonde-vlight-newborn-neutral-sticker.png', alt: 'Light blonde character' },
  { src: '/images/characters/straight-black-asian-newborn-neutral-sticker.png', alt: 'Asian character' },
  { src: '/images/characters/short-straight-dark-brown-newborn-neutral-sticker.png', alt: 'Brown-haired character' },
  { src: '/images/characters/curly-black-med-newborn-neutral-sticker.png', alt: 'Curly-haired character' },
  { src: '/images/characters/wavy-blonde-fair-newborn-neutral-sticker.png', alt: 'Wavy-haired character' },
]

export const PersonalisationShowcase: React.FC = () => (
  <StyledSection>
    <Container maxWidth="lg">
      <StyledGrid>
        <StyledTextColumn>
          <StyledEyebrow variant="overline">Character Library</StyledEyebrow>
          <StyledHeadline variant="h2">
            Create unlimited character stickers and variants for your storybooks.
          </StyledHeadline>

          <StyledStepsList>
            {steps.map((step) => (
              <StyledStep key={step.number}>
                <StyledStepNumber>{step.number}</StyledStepNumber>
                <Box>
                  <StyledStepTitle variant="h6">{step.title}</StyledStepTitle>
                  <StyledStepDescription variant="body2">
                    {step.description}
                  </StyledStepDescription>
                </Box>
              </StyledStep>
            ))}
          </StyledStepsList>
        </StyledTextColumn>

        <StyledCharacterColumn>
          <StyledCharacterGrid>
            {characters.map((character) => (
              <StyledCharacterCard key={character.src}>
                <Image
                  src={character.src}
                  alt={character.alt}
                  width={160}
                  height={160}
                  style={{ objectFit: 'contain', width: '100%', height: 'auto' }}
                />
              </StyledCharacterCard>
            ))}
          </StyledCharacterGrid>
        </StyledCharacterColumn>

        <StyledCtaWrapper>
          <StyledCtaButton arrow primary size="large" href="/shop">
            Browse Stories
          </StyledCtaButton>
        </StyledCtaWrapper>
      </StyledGrid>
    </Container>
  </StyledSection>
)

const StyledSection = styled('section')(({ theme }) => ({
  padding: theme.spacing(5, 0),
  background: 'linear-gradient(135deg, #FFF8F2 0%, #FFF0E6 100%)',

  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(8, 0),
  },
}))

const StyledGrid = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(5),

  [theme.breakpoints.up('md')]: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows: 'auto auto',
    gap: theme.spacing(8),
    alignItems: 'center',
  },
}))

const StyledTextColumn = styled('div')(({ theme }) => ({
  textAlign: 'center',

  [theme.breakpoints.up('md')]: {
    gridColumn: 1,
    gridRow: 1,
    textAlign: 'left',
  },
}))

const StyledEyebrow = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 700,
  fontSize: pxToRem(16),
  letterSpacing: pxToRem(2),
  marginBottom: theme.spacing(2),
}))

const StyledHeadline = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(4),
  lineHeight: 1.3,
}))

const StyledStepsList = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
}))

const StyledStep = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(2),
  textAlign: 'left',
}))

const StyledStepNumber = styled('div')(({ theme }) => ({
  width: pxToRem(36),
  height: pxToRem(36),
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 700,
  fontSize: pxToRem(16),
  flexShrink: 0,
}))

const StyledStepTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(0.5),
}))

const StyledStepDescription = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  lineHeight: 1.6,
}))

const StyledCtaWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',

  [theme.breakpoints.up('md')]: {
    gridColumn: 1,
    gridRow: 2,
    justifyContent: 'flex-start',
  },
}))

const StyledCtaButton = styled(Button)(({ theme }) => ({
  color: theme.palette.secondary.main,
  fontWeight: 500,
  padding: theme.spacing(2.5, 4),
}))

const StyledCharacterColumn = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',

  [theme.breakpoints.up('md')]: {
    gridColumn: 2,
    gridRow: '1 / 3',
    justifyContent: 'flex-end',
  },
}))

const StyledCharacterGrid = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: theme.spacing(2),
  maxWidth: pxToRem(420),
  width: '100%',
}))

const StyledCharacterCard = styled('div')(({ theme }) => ({
  borderRadius: pxToRem(12),
  overflow: 'hidden',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.02)',
  backgroundColor: '#fff',
  padding: theme.spacing(1.5),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}))
